import type { IDateTimeFormat } from '@/objects/IDateTimeFormat';
import WellKnown from '@/objects/generated/static/WellKnown';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import { t } from 'i18next';
import type { Eventinfo, EventinfoBase, Sport } from '@/objects/generated';

export type TimeType = 'TIME' | 'TIMEMS1' | 'TIMEMS2' | 'TIMEMS3';

export type DateType =
    | 'DATE'
    | 'DATETIME'
    | 'DATETIMEMS1'
    | 'DATETIMEMS2'
    | 'DATETIMEMS3'
    | 'DATETIMESHORT'
    | 'DAYTIME'
    | 'MONTHLONG'
    | 'YEARONLY'
    | 'COMLETETEXTDATEYEAR'
    | TimeType;

/**
 * Lookup between the DateType and the Intl.DateTimeFormatOptions
 */
export const DateFormats: Record<DateType, IDateTimeFormat> = {
    DATE: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        format: 'yyyy-MM-dd',
    },
    TIME: {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        format: 'hh:mm:ss',
    },
    DATETIME: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        format: 'yyyy-MM-dd hh:mm:ss',
    },
    DATETIMESHORT: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        format: 'yyyy-MM-dd hh:mm',
    },
    DATETIMEMS1: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 1,
        hour12: false,
        format: 'yyyy-MM-dd hh:mm:ss.f',
    },
    DATETIMEMS2: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 2,
        hour12: false,
        format: 'yyyy-MM-dd hh:mm:ss.ff',
    },
    DATETIMEMS3: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
        hour12: false,
        format: 'yyyy-MM-dd hh:mm:ss.fff',
    },
    TIMEMS1: {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 1,
        hour12: false,
        format: 'hh:mm:ss.f',
    },
    TIMEMS2: {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 2,
        hour12: false,
        format: 'hh:mm:ss.ff',
    },
    TIMEMS3: {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
        hour12: false,
        format: 'hh:mm:ss.fff',
    },
    MONTHLONG: {
        month: 'long',
        format: 'MMMM',
    },
    YEARONLY: {
        year: 'numeric',
        format: 'yyyy',
    },
    DAYTIME: {
        weekday: 'long',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        format: 'EEEE hh:mm',
    },
    COMLETETEXTDATEYEAR: {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        weekday: 'long',
        format: 'EEEE dd MMMM yyyy',
    },
};

/**
 * Returns a Intl.DateTimeFormatOptions object with sensible values for the current DateType
 * @param type The DateType we want to find formatoptions for
 */
export const DateTypeToFormatParams = (type: DateType): Intl.DateTimeFormatOptions => DateFormats[type];

/**
 * The default Date object with values that should be used if we have an incomplete format
 * Any DateType that does not have the date part will use the date from this object
 * Any DateType that does not have the time part will use the time from this object
 */
const defaultDateTimeForIncompleteFormats = new Date('0001-01-01T00:00:00.000');

const dateFormatsWithoutTime = ['DATE'];
const dateFormatsWithoutDate = ['TIME', 'TIMEMS1', 'TIMEMS2', 'TIMEMS3'];
const dateFormatsWithoutMilliseconds = ['DATE', 'DATETIME', 'TIME'];

/**
 * Converts a DateType to its corresponding time only type
 * @param type The type to convert
 * @returns The corresponding time only type
 */
export const dateTypeToTimeOnlyType = (type: DateType): DateType => {
    switch (type) {
        case 'DATETIME':
            return 'TIME';
        case 'DATETIMEMS1':
            return 'TIMEMS1';
        case 'DATETIMEMS2':
            return 'TIMEMS2';
        case 'DATETIMEMS3':
            return 'TIMEMS3';
        default:
            return type;
    }
};

/**
 * Converts a DateType to its corresponding full date type
 * @param type The type to convert
 * @returns The corresponding full date type
 */
export const dateTypeToFullDateType = (type: DateType): DateType => {
    switch (type) {
        case 'TIME':
            return 'DATETIME';
        case 'TIMEMS1':
            return 'DATETIMEMS1';
        case 'TIMEMS2':
            return 'DATETIMEMS2';
        case 'TIMEMS3':
            return 'DATETIMEMS3';
        default:
            return type;
    }
};

export const dateFormatHasDate = (type: DateType) => !dateFormatsWithoutDate.includes(type);

export const dateFormatHasTime = (type: DateType) => !dateFormatsWithoutTime.includes(type);

/**
 * Checks if 2 dates are on the same day.
 *
 * Checks year, month and day. Time is ignored
 * @param d1
 * @param d2
 * @returns true if d1 and d2 are on the same day, false otherwise
 */
export const isSameDate = (d1: Date, d2: Date) =>
    d1.getFullYear() == d2.getFullYear() && d1.getMonth() == d2.getMonth() && d1.getDate() == d2.getDate();

/**
 * Checks if 2 dates are in the same month
 *
 * Checks year and month. Day and time is ignored
 * @param d1
 * @param d2
 * @returns true if d1 and d2 are in the same month, false otherwise
 */
export const isSameMonth = (d1: Date, d2: Date) => d1.getFullYear() == d2.getFullYear() && d1.getMonth() == d2.getMonth();

/**
 * Gets all dates for the month of inputDate
 * @param inputDate
 * @returns An array of Date objects representing every day in the month
 */
export const getDatesInMonth = (inputDate: Date) => {
    // Date that represents the first day of the current month
    const d = new Date(inputDate.getFullYear(), inputDate.getMonth(), 1);
    const monthToCheck = d.getMonth();
    const dates: Date[] = [];
    // Every day in this month
    while (monthToCheck == d.getMonth()) {
        dates.push(new Date(d));
        d.setDate(d.getDate() + 1);
    }
    return dates;
};

/**
 * Get all months represented as Date objects so we can translate using locize easily
 * @return An array of Date objects for the first day of each month of the current year
 */
export const getMonthsInYear = () => {
    const date = new Date();
    date.setMonth(0);
    date.setDate(1);

    const months: Date[] = [];
    while (months.length < 12) {
        // js dates start at 0, so the last month is 11, not 12. Thats why we stop before 12 here
        months.push(new Date(date));
        date.setMonth(date.getMonth() + 1);
    }
    return months;
};

/**
 * Formats a date to an ISO string
 *
 * Reference:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toISOString
 * @param date The date we want to format
 * @returns A string in the format 'YYYY-MM-DDTHH:mm:ss.sss'
 */
export const formatDateIso = (date: Date): string => {
    const cloneDate = new Date(date);
    cloneDate.setMinutes(cloneDate.getMinutes() + cloneDate.getTimezoneOffset() * -1);
    const datestring = cloneDate.toISOString(); // This is UTC, so FUCK
    if (datestring.length == 27) {
        // ±YYYYYY-MM-DDTHH:mm:ss.sssZ
        return datestring.substring(3, 26);
    }

    if (datestring.length == 24) {
        // YYYY-MM-DDTHH:mm:ss.sssZ
        return datestring.substring(0, 23);
    }

    throw `Something is very wrong, datestring is ${datestring.length} long! Expected 27 or 24`;
};

/**
 * Formats a date to an JSON time string
 * @param date The date we want to format
 */
export const formatDateToTimeJson = (date: Date) => {
    return formatDateIso(date)?.substring(11);
};

/**
 * This formats a date in the default format that the browser wants.
 *
 * For norwegian this will be 'dd.MM.yyyy, hh:mm:ss,fff'
 *
 * For english this will be   'MM/dd/yyyy, hh:mm:ss,fff'
 * @param date The date we want to format
 * @param type The DateType we want to format as
 * @returns A string with the date in whatever format fits the current language
 */
export const formatDateDefault = (date: Date | null, type: DateType) => {
    if (date == null) return '';

    return t('{{val, datetime}}', {
        val: date,
        formatParams: { val: DateTypeToFormatParams(type) },
        interpolation: { escapeValue: false },
    });
};

/**
 * This formats a date to a more useable format for inputs
 *
 * Specifically this removes the trailing comma after year and replaces all other commas with periods
 *
 * For norwegian this will give 'dd.MM.yyyy hh:mm:ss.fff'
 *
 * For english this will give   'MM/dd/yyyy hh:mm:ss.fff'
 * @param date The date we want to format
 * @param type The DateType we want to format as
 * @returns A string with the date in a format that is more useable for inputs
 */
export const formatDateToInputFormat = (date: Date | null, type: DateType) => {
    if (date == null) {
        return '';
    }

    let inital = t('{{val, datetime}}', {
        val: date,
        formatParams: { val: DateTypeToFormatParams(type) },
        interpolation: { escapeValue: false },
    });

    // We get "2024-12-12, 01:01:01,000" from the t so we remove the comma after the yeat
    inital = inital.replace(`${date.getFullYear().toString()},`, date.getFullYear().toString());
    // then we replace the comma ms separator with a dot
    inital = inital.replaceAll(',', '.');

    return inital;
};

/**
 * Parses a date from a ISO format, use to parse data from the server
 * This also accepts a Date object as this is used where we used to parse date
 */
export const parseDateStringISO = (dateString: string | Date): Date => {
    return new Date(dateString);
};

/**
 * Creates a new Date object with the datepart from the {@link defaultDateTimeForIncompleteFormats} and the timepart from the {@link time}
 * @param time A Date object containing the time we want to set
 * @returns A new Date with the datepart from the {@link defaultDateTimeForIncompleteFormats} and the timepart from the {@link time}
 */
export const getDefaultDateWithTime = (time: Date) => {
    const d = new Date(defaultDateTimeForIncompleteFormats.getTime());
    d.setHours(time.getHours());
    d.setMinutes(time.getMinutes());
    d.setSeconds(time.getSeconds());
    d.setMilliseconds(time.getMilliseconds());
    return d;
};

/**
 * Creates a new Date object with the timepart from the {@link defaultDateTimeForIncompleteFormats} and the datepart from the {@link date}
 * @param date A Date object containing the date we want to set
 * @returns A new Date with the timepart from the {@link defaultDateTimeForIncompleteFormats} and the datepart from the {@link date}
 */
export const getDefaultDateWithDate = (date: Date) => {
    const d = new Date(defaultDateTimeForIncompleteFormats.getTime());
    d.setFullYear(date.getFullYear());
    d.setMonth(date.getMonth());
    d.setDate(date.getDate());
    return d;
};

/**
 * Creates a new Date object with the datepart from the {@link date} and the timepart from the {@link timePart}
 * @param datePart The date representing the date part
 * @param timePart The date representing the time part
 * @returns A new Date object with the date from the {@link datePart} and the time from the {@link timePart}
 */
export const mergeDatePartAndTimePart = (datePart: Date, timePart: Date) => {
    const d = new Date();
    d.setFullYear(datePart.getFullYear());
    d.setMonth(datePart.getMonth());
    d.setDate(datePart.getDate());
    d.setHours(timePart.getHours());
    d.setMinutes(timePart.getMinutes());
    d.setSeconds(timePart.getSeconds());
    d.setMilliseconds(timePart.getMilliseconds());
    return d;
};

/**
 * Will return the date with the time part set to 00:00:00.000
 * @param date
 * @returns
 */
export const getDateWithMinTime = (date: Date) => {
    const time = new Date();
    time.setHours(0, 0, 0, 0); // Set time to 00:00:00.000

    return mergeDatePartAndTimePart(date, time);
};

/**
 * Will return the date with the time part set to 23:59:59.999
 * @param date
 * @returns
 */
export const getDateWithMaxTime = (date: Date) => {
    const time = new Date();
    time.setHours(23, 59, 59, 999); // Set time to 23:59:59.999

    return mergeDatePartAndTimePart(date, time);
};

/**
 * Parses a date from a possibly incomplete string and overwrites filled values in the original date.
 *
 * Example:
 *
 * dateString: "01.10.1991 hh:mm:ss.fff" and format: "dd.MM.yyyy hh:mm:ss.fff" will set the date of the originalDate but leave the timepart unchanged
 * @param dateString The string containing the date
 * @param {DateType} type The DateType to find the format from
 * @param originalDate The date to modify
 * @return A new Date object matching the original date, but overwritten with the valid parts from the dateString
 */
export const parseDateStringIntoDateFromCurrentFormat = (
    dateString: string,
    type: DateType,
    originalDate: Date,
    defaultDateForTimeOnlyInput?: Date,
) => {
    const date = parseDateStringIntoDateFromFormat(dateString, getCurrentDateFormat(type), originalDate);

    if (dateFormatsWithoutTime.includes(type)) {
        date.setHours(
            defaultDateTimeForIncompleteFormats.getHours(),
            defaultDateTimeForIncompleteFormats.getMinutes(),
            defaultDateTimeForIncompleteFormats.getSeconds(),
            defaultDateTimeForIncompleteFormats.getMilliseconds(),
        );
    }

    if (dateFormatsWithoutDate.includes(type)) {
        const dateForTimeOnly = defaultDateForTimeOnlyInput ?? defaultDateTimeForIncompleteFormats;

        date.setFullYear(dateForTimeOnly.getFullYear(), dateForTimeOnly.getMonth(), dateForTimeOnly.getDate());
    }

    if (dateFormatsWithoutMilliseconds.includes(type)) {
        date.setMilliseconds(defaultDateTimeForIncompleteFormats.getMilliseconds());
    }

    return date;
};

/**
 * Parses a date from a possibly incomplete string and overwrites filled values in the original date
 *
 * @example
 * ```
 * const res = parseDateStringIntoDateFromFormat("01.10.1991 hh:mm:ss.fff", "dd.MM.yyyy hh:mm:ss.fff", new Date('1991-12-02 12:12:12'));
 * // res = 1991-10-01 12:12:12.000
 * ```
 * dateString: "01.10.1991 hh:mm:ss.fff" and format: "dd.MM.yyyy hh:mm:ss.fff" will set the date of the originalDate but leave the timepart unchanged
 * @param dateString The string containing the date
 * @param format The full format of the datestring
 * @param originalDate The date to modify
 * @return A new Date object matching the original date, but overwritten with the valid parts from the dateString
 */
export const parseDateStringIntoDateFromFormat = (dateString: string, format: string, originalDate: Date): Date => {
    const returnDate = new Date(originalDate);

    let reg = format.replace('yyyy', '(?<year>[0-9]{4})');
    reg = reg.replace('MM', '(?<month>[0-9]{2})');
    reg = reg.replace('dd', '(?<day>[0-9]{2})');
    reg = reg.replace('hh', '(?<hour>([0-9]|h){2})');
    reg = reg.replace('mm', '(?<minute>([0-9]|m){2})');
    reg = reg.replace('ss', '(?<second>([0-9]|s){2})');

    // Check how many ms digits we have
    if (format.includes('fff')) {
        reg = reg.replace('fff', '(?<ms>([0-9]|f){3})');
    } else if (format.includes('ff')) {
        reg = reg.replace('ff', '(?<ms>([0-9]|f){2})');
    } else if (format.includes('f')) {
        reg = reg.replace('f', '(?<ms>([0-9]|f){1})');
    }

    const regexp = new RegExp(reg);

    const matches = regexp.exec(dateString);

    if (!matches || !matches.groups) {
        return returnDate;
    }

    const year = parseInt(matches.groups.year);
    const month = parseInt(matches.groups.month);
    const day = parseInt(matches.groups.day);

    // If we cant parse the date then we have nothing to update so we keep the date as it is, because the format can not include date if we want...
    if (![year, month, day].includes(NaN)) {
        // Set date, we are guaranteed to have this here
        returnDate.setFullYear(year, month - 1, day);
    }

    const hm = matches.groups.hour;
    const mm = matches.groups.minute;
    const sm = matches.groups.second;

    // The hour can be 'hh', 'h1','1h','10' or undefined.
    // If undefined we dont want to overwrite the hour.
    // If there is a leftover 'h' in the format then the position of the 'h' matters.
    // If its 'hh' we just want NaN so we dont overwrite the hour with 0 (even tho its likely to be 0 already)
    const hour = hm && !isNaN(parseInt(hm)) ? parseInt(hm.replaceAll('h', '0')) : NaN;
    const minute = mm && !isNaN(parseInt(mm)) ? parseInt(mm.replaceAll('m', '0')) : NaN;
    const second = sm && !isNaN(parseInt(sm)) ? parseInt(sm.replaceAll('s', '0')) : NaN;

    // Set time part to what is valid
    returnDate.setHours(isNaN(hour) ? originalDate.getHours() : hour);
    returnDate.setMinutes(isNaN(minute) ? originalDate.getMinutes() : minute);
    returnDate.setSeconds(isNaN(second) ? originalDate.getSeconds() : second);

    const msDecimals = format.substring(format.indexOf('f')).length; // Count number of f's at the end of the string
    let ms = matches.groups.ms && !isNaN(parseInt(matches.groups.ms)) ? parseInt(matches.groups.ms) : NaN;

    if (!isNaN(ms)) {
        ms = ms * Math.pow(10, 3 - msDecimals); // .1 should be .100, .10 should be .100, .100 is already .100
    }

    returnDate.setMilliseconds(isNaN(ms) ? originalDate.getMilliseconds() : ms);

    return returnDate;
};

/**
 * Convert the format to a regex and return the matches
 * @param format
 * @param datetime
 * @returns
 */
const parseDateTimeFormat = (
    format: string,
    datetime: string,
): {
    year: number | null;
    month: number | null;
    day: number | null;
    hour: number | null;
    minute: number | null;
    second: number | null;
    fraction: number | null;
} | null => {
    let reg = format.replace('yyyy', '(?<year>[0-9]{4})');
    reg = reg.replace('MM', '(?<month>[0-9]{2})');
    reg = reg.replace('dd', '(?<day>[0-9]{2})');
    reg = reg.replace('hh', '(?<hour>([0-9]|h){2})');
    reg = reg.replace('mm', '(?<minute>([0-9]|m){2})');
    reg = reg.replace('ss', '(?<second>([0-9]|s){2})');

    // Check how many ms digits we have
    if (format.includes('fff')) {
        reg = reg.replace('fff', '(?<ms>([0-9]|f){3})');
    } else if (format.includes('ff')) {
        reg = reg.replace('ff', '(?<ms>([0-9]|f){2})');
    } else if (format.includes('f')) {
        reg = reg.replace('f', '(?<ms>([0-9]|f){1})');
    }

    const regexp = new RegExp(reg);

    const matches = regexp.exec(datetime);

    if (!matches || !matches.groups) {
        return null;
    }

    const year = parseInt(matches.groups.year);
    const month = parseInt(matches.groups.month);
    const day = parseInt(matches.groups.day);
    const hour = parseInt(matches.groups.hour);
    const minute = parseInt(matches.groups.minute);
    const second = parseInt(matches.groups.second);
    const fraction = parseInt(matches.groups.ms);

    return {
        year: isNaN(year) ? null : year,
        month: isNaN(month) ? null : month,
        day: isNaN(day) ? null : day,
        hour: isNaN(hour) ? null : hour,
        minute: isNaN(minute) ? null : minute,
        second: isNaN(second) ? null : second,
        fraction: isNaN(fraction) ? null : fraction,
    };
};

/**
 *
 * @param timespan
 * @param timetype
 * @returns
 */
export const parseTimespanIntoStringFromFormat = (timespan: ITimeSpan | null, timetype: TimeType): string => {
    if (timespan == null) {
        return '';
    }

    const format = DateFormats[timetype]?.format;

    // We count the number of f's in the format to determine how many digits we need to show
    const numberOfFractions = format?.substring(format.indexOf('f')).length ?? 0;

    const parsed = parseDateTimeFormat(format, millisecondsToFormattedTime(timespan.TotalMilliseconds, timetype));

    return (
        (timespan.IsNegative ? '-' : '') +
        format
            .replace('dd', (parsed?.day ?? 0)?.toString())
            .replace('hh', (parsed?.hour ?? 0).toString().padStart(2, '0'))
            .replace('mm', (parsed?.minute ?? 0).toString().padStart(2, '0'))
            .replace('ss', (parsed?.second ?? 0).toString().padStart(2, '0'))
            .replace('fff', (parsed?.fraction ?? 0).toString().padStart(numberOfFractions, '0'))
            .replace('ff', (parsed?.fraction ?? 0).toString().padStart(numberOfFractions, '0'))
            .replace('f', (parsed?.fraction ?? 0).toString().padStart(numberOfFractions, '0'))
    );
};

/**
 * Return the Time part of a Date as a TimeSpan
 * @param date The date we want to extract the time from
 * @returns A TimeSpan representing the time part of the date
 */
export const getTimespanFromDate = (date: Date): TimeSpan => {
    const totalMilliseconds =
        date.getHours() * 60 * 60 * 1000 + date.getMinutes() * 60 * 1000 + date.getSeconds() * 1000 + date.getMilliseconds();
    return TimeSpan.FromMilliseconds(totalMilliseconds);
};

/**
 * Finds the current dateformat for a given DateType
 * @param type The DateType we want to find the current format for
 * @returns The current dateformat for the given DateType
 */
export const getCurrentDateFormat = (type: DateType): string => {
    const date = new Date();
    date.setFullYear(9999);
    date.setMonth(11); // Remember that the months are 0 indexed, so this is december
    date.setDate(31);
    date.setHours(11); // We want an hour that is less than 12 so 24 hour and 12 hour clocks show the same
    date.setMinutes(59);
    date.setSeconds(48);
    date.setMilliseconds(777);

    let dateString = formatDateToInputFormat(date, type);

    dateString = dateString.replace('9999', 'yyyy');
    dateString = dateString.replace('12', 'MM');
    dateString = dateString.replace('31', 'dd');
    dateString = dateString.replace('11', 'hh');
    dateString = dateString.replace('59', 'mm');
    dateString = dateString.replace('48', 'ss');
    dateString = dateString.replace(/7/g, 'f');

    return dateString;
};

/**
 * Will return number of seconds the clockpart of a Date represents
 * 2024-12-12 01:01:01.000 will return 3661
 * 1970-01-01 01:01:01.000 will also return 3661
 * @param val
 * @returns
 */
export const dateTimeToSeconds = (val: Date) => {
    return val.getHours() * 60 * 60 + val.getMinutes() * 60 + val.getSeconds();
};
/**
 * Return the number of milliseconds the clockpart of a Date represents
 * 2024-12-12 01:01:01.001 will return 3661001
 * 1970-01-01 01:01:01.001 will also return 3661001
 * @param val
 * @returns
 */
export const dateTimeToMilliseconds = (val: Date) => {
    return dateTimeToSeconds(val) * 1000 + val.getMilliseconds();
};

/**
 * Tryes to parse a string as a date without giving an exception
 * @param val a string that we want to parse as a date
 * @returns A Date object if the string could be parsed as a date, null otherwise
 */
export const createDate = (val: string | undefined | null): Date | null => {
    if (val === null) {
        return null;
    }

    if (val === undefined) {
        return null;
    }

    try {
        return new Date(val);
    } catch (ex) {
        console.warn(`Could not parse ${val} as a date`);
        return null;
    }
};

/**
 * Will make a Date from seconds
 *
 * The date is relative to the {@link defaultDateTimeForIncompleteFormats}
 *
 * 10 seconds will return 0001-01-01 00:00:10.000
 * @param val number of seconds
 * @returns a Date object with the time set to the input value
 */
export const secondsToJsDate = (val: number) => {
    return millisecondsToJsDate(val * 1000);
};

/**
 * Creates a new Date from the given milliseconds
 *
 * The date is relative to the {@link defaultDateTimeForIncompleteFormats}
 *
 * 100 ms will return 0001-01-01 00:00:00.100
 * @param ms The milliseconds we want to create a Date object from
 * @returns A new Date object with the time set to the input value
 */
export const millisecondsToJsDate = (ms: number) => {
    return new Date(defaultDateTimeForIncompleteFormats.getTime() + ms);
};

/**
 * Converts a Date object to milliseconds relative to {@link defaultDateTimeForIncompleteFormats}
 *
 * This is how we are representing the AccumulatedTime when we need a datetime representation of it.
 *
 * TODO: Maybe this should be renamed. Usually we would just use the ticks since 1970.
 * Maybe we should change the defaultDateTimeForIncompleteFormats to 1970-01-01 00:00:00.000 (?)
 *
 * @param date The date we want to find the milliseconds of
 * @returns The number of milliseconds since {@link defaultDateTimeForIncompleteFormats}
 */
export const dateToMilliseconds = (date: Date) => {
    return date.getTime() - defaultDateTimeForIncompleteFormats.getTime();
};

/**
 * Returns a Date object set to the same date as the {@link date} parameter and the time set to the current time
 * @param date The Date we want to change the date of
 * @returns A new Date object with the date set to the same date as the input {@link date} and the time set to the current time
 */
export const dateWithCurrentTime = (date: Date): Date => {
    const result = new Date();
    result.setFullYear(date.getFullYear());
    result.setMonth(date.getMonth());
    result.setDate(date.getDate());

    return result;
};

/**
 * Shorthand for isSameDate(datetime, new Date())
 * @param datetime
 * @returns
 */
export const isToday = (datetime: Date): boolean => {
    return isSameDate(datetime, new Date());
};

/**
 * Creates a new Date oject with the time of {@link time} and the date of {@link datetime}
 * @param time A Date object that contains the time of the result
 * @param datetime A Date object that contains the date of the result
 * @returns A new Date object with the time of time and the date of datetime
 */
export const addTimeToDate = (time: Date | null, datetime: Date | null): Date | null => {
    if (time == null || datetime == null) {
        return null;
    }

    return new Date(
        datetime.getFullYear(),
        datetime.getMonth(),
        datetime.getDate(),
        time.getHours(),
        time.getMinutes(),
        time.getSeconds(),
        time.getMilliseconds(),
    );
};

/**
 * Adds the timespan to a date/datetime
 * @param time
 * @param datetime
 * @returns
 */
export function addTimespanToDate(time: ITimeSpan, datetime: Date): Date;
export function addTimespanToDate(time: ITimeSpan | null, datetime: Date | null): Date | null;
export function addTimespanToDate(time: ITimeSpan | null, datetime: Date | null): Date | null {
    if (time == null || datetime == null) {
        return null;
    }
    return new Date(datetime.getTime() + time.TotalMilliseconds);
}

export function addTimespanToTime(time: ITimeSpan, time2: ITimeSpan): TimeSpan;
export function addTimespanToTime(time: ITimeSpan | null, time2: ITimeSpan | null): TimeSpan | null {
    if (time == null || time2 == null) {
        return null;
    }
    return TimeSpan.FromMilliseconds(time.TotalMilliseconds + time2.TotalMilliseconds);
}

/**
 * Finds the difference between two Date objects as a Date object relative to {@link defaultDateTimeForIncompleteFormats}
 * @param firstTime Time to diff
 * @param secondTime Time to diff
 * @returns A new Date object with the difference between firstTime and secondTime expressed as a Date object relative to {@link defaultDateTimeForIncompleteFormats}
 */
export function timeDiffAsDateTime(firstTime: Date, secondTime: Date): Date;
export function timeDiffAsDateTime(firstTime: Date | null, secondTime: Date | null): Date | null;
export function timeDiffAsDateTime(firstTime: Date | null, secondTime: Date | null): Date | null {
    if (firstTime == null || secondTime == null) {
        return null;
    }
    const date = new Date(defaultDateTimeForIncompleteFormats);
    date.setMilliseconds(firstTime.getTime() - secondTime.getTime());
    return date;
}

/**
 * Finds the difference between two Date objects as milliseconds
 * @param firstTime Time to diff
 * @param secondTime Time to diff
 * @returns The difference between firstTime and secondTime in milliseconds
 */
export const timeDiffAsMilliseconds = (firstTime: Date | null, secondTime: Date | null): number | null => {
    if (firstTime == null || secondTime == null) {
        return null;
    }

    return Math.abs(firstTime.getTime() - secondTime.getTime());
};

/**
 * Finds the difference between two Date objects as timespan
 * @param firstTime Time to diff
 * @param secondTime Time to diff
 * @returns The difference between firstTime and secondTime as timespan
 */
export function timeDiffAsTimespan(firstTime: Date, secondTime: Date): ITimeSpan;
export function timeDiffAsTimespan(firstTime: Date | null, secondTime: Date | null): ITimeSpan | null;
export function timeDiffAsTimespan(firstTime: Date | null, secondTime: Date | null): ITimeSpan | null {
    if (firstTime == null || secondTime == null) {
        return null;
    }

    return TimeSpan.FromMilliseconds(firstTime.getTime() - secondTime.getTime());
}

/**
 * Will try and parse a datestring with the given format
 * @param dateString
 * @param format The format of the datestring based on the values y m d this can be in any order
 * @returns A Date object if the string could be parsed as a date, null otherwise
 */
export const tryParseDatestring = (dateString: string, format: string): Date | null => {
    let yearIndex = 0;
    let monthIndex = 1;
    let dayIndex = 2;

    // We check if the format is a simple format like 'ymd' or 'dmy' or 'mdy' without any separators
    // If it is, we need to assume that the number of characters define the number of digits per value
    if (/^[ymdYMD:]+$/.test(format)) {
        // We need to assume that the number of characters define the number of digits
        const characterMap: Map<string, number> = new Map();
        const order: string[] = [];
        const valueCharacters = format.toLowerCase().split('');
        for (const char of valueCharacters) {
            if (!characterMap.has(char)) {
                characterMap.set(char, 1);
                order.push(char);
            } else {
                characterMap.set(char, characterMap.get(char)! + 1);
            }
        }

        format = order.join(':');

        // We need to fix the value to match the format
        const orderedCharacters: string[] = [];
        let currentIndex = 0;
        for (const [_, characters] of characterMap.entries()) {
            orderedCharacters.push(dateString.substring(currentIndex, currentIndex + characters));
            currentIndex += characters;
        }

        dateString = orderedCharacters.join(':');
    }

    const values: string[] = format
        .toLowerCase()
        .replace(/[^dym]/g, ':')
        .split(':')
        .filter((i) => i);

    // Here we need to iterate the values and keep only 1 character per value
    // This will help us later to identify the indexes of y m d
    for (let i = 0; i < values.length; i++) {
        values[i] = values[i][0];
    }

    // We always expect 3 values and they should contain y m d
    if (values.length !== 3 || !values.includes('y') || !values.includes('m') || !values.includes('d')) {
        return null;
    }

    // Find the index of the y m d values
    values.forEach((v, i) => {
        if (v.includes('y')) {
            yearIndex = i;
        } else if (v.includes('m')) {
            monthIndex = i;
        } else if (v.includes('d')) {
            dayIndex = i;
        }
    });

    const _value: number[] = dateString
        .replace(/\D/g, ':')
        .split(':')
        .filter((i) => i)
        .map((i) => parseInt(i));

    if (_value.length !== 3) {
        return null;
    }

    // normalize values
    let year: number = _value[yearIndex];

    const currentYear: number = new Date().getFullYear() - 2000;
    if (year < currentYear) {
        year += 2000;
    } else if (year < 100) {
        year += 1900;
    }

    if (
        year < 1899 ||
        year > currentYear + 2100 || // This is just so we dont get a year that is too far in the future as it makes no sence and is probably a mistake
        _value[monthIndex] < 1 ||
        _value[monthIndex] > 12 ||
        _value[dayIndex] < 1 ||
        _value[dayIndex] > 31
    ) {
        return null;
    }

    return new Date(year, _value[monthIndex] - 1, _value[dayIndex]);
};

/**
 * Will try and parse a timestring with the given format
 * @param timeString
 * @param format The format of the timestring based on the values h m s
 * @returns A Date object if the string could be parsed as a date, null otherwise and the date will be set to 0001-01-01
 */
export const tryParseTimestring = (timeString: string, format: string): Date | null => {
    let hourIndex = -1;
    let minuteIndex = -1;
    let secondIndex: number | null = null;
    let millisecondsIndex: number | null = null;
    let yearIndex: number | null = null;
    let monthIndex: number | null = null;
    let dayIndex: number | null = null;

    const formatParts = format.toLowerCase().match(/[a-z]+|[^a-z]+/g);
    if (formatParts == null) {
        return null;
    }

    let splitChar = '';
    let valueIndex = 0;

    formatParts.forEach((part) => {
        if (/^[a-z]+$/.test(part)) {
            const token = part[0];

            if (token === 'm') {
                // m means month when dot-separated and minute when colon-separated.
                if (splitChar.includes('.')) {
                    monthIndex = valueIndex;
                } else {
                    minuteIndex = valueIndex;
                }
            } else if (token === 'd') {
                dayIndex = valueIndex;
            } else if (token === 'y') {
                yearIndex = valueIndex;
            } else if (token === 'h') {
                hourIndex = valueIndex;
            } else if (token === 's') {
                secondIndex = valueIndex;
            } else if (token === 'f') {
                millisecondsIndex = valueIndex;
            }

            valueIndex++;
            return;
        }

        splitChar = part;
    });

    if (hourIndex < 0 || minuteIndex < 0) {
        return null;
    }

    const _value: number[] = timeString
        .replace(/\D/g, ':')
        .split(':')
        .filter((i) => i)
        .map((i) => parseInt(i));

    const hasDate = dayIndex != null || monthIndex != null || yearIndex != null;
    const hasFullDate = dayIndex != null && monthIndex != null && yearIndex != null;
    if (hasDate && !hasFullDate) {
        return null;
    }

    const hasSeconds = secondIndex != null;

    // We expect h:m and optionally s/f, with optional d.m.y prefix.
    const expectedValues = 2 + (hasSeconds ? 1 : 0) + (millisecondsIndex != null ? 1 : 0) + (hasFullDate ? 3 : 0);
    const minExpectedValues = expectedValues - (hasSeconds ? 1 : 0);
    if (_value.length !== expectedValues && _value.length !== minExpectedValues) {
        return null;
    }

    const parsedValues = [..._value];
    if (hasSeconds && _value.length === minExpectedValues && secondIndex != null) {
        // When seconds are omitted, insert 0 to keep indexes aligned.
        parsedValues.splice(secondIndex, 0, 0);
    }

    // We check if we have the correct number of values, if we have milliseconds we expect 4 values or else we default the milliseconds to .000
    let millisecondsString: string = '000';
    if (millisecondsIndex != null) {
        if (parsedValues[millisecondsIndex] < 0 || parsedValues[millisecondsIndex] > 999) {
            return null;
        }
        millisecondsString = parsedValues[millisecondsIndex].toString().padEnd(3, '0');
    }

    let yearString = '2001';
    let monthString = '01';
    let dayString = '01';

    if (hasFullDate && yearIndex != null && monthIndex != null && dayIndex != null) {
        const inputYear = parsedValues[yearIndex];
        const inputMonth = parsedValues[monthIndex];
        const inputDay = parsedValues[dayIndex];

        const currentYear: number = new Date().getFullYear() - 2000;
        let normalizedYear = inputYear;
        if (normalizedYear < currentYear) {
            normalizedYear += 2000;
        } else if (normalizedYear < 100) {
            normalizedYear += 1900;
        }

        if (normalizedYear < 1899 || inputMonth < 1 || inputMonth > 12 || inputDay < 1 || inputDay > 31) {
            return null;
        }

        yearString = normalizedYear.toString().padStart(4, '0');
        monthString = inputMonth.toString().padStart(2, '0');
        dayString = inputDay.toString().padStart(2, '0');
    }

    if (
        parsedValues[hourIndex] < 0 ||
        parsedValues[hourIndex] > 23 ||
        parsedValues[minuteIndex] < 0 ||
        parsedValues[minuteIndex] > 59 ||
        (secondIndex != null && (parsedValues[secondIndex] < 0 || parsedValues[secondIndex] > 59))
    ) {
        return null;
    }

    const hourString = parsedValues[hourIndex].toString().padStart(2, '0');
    const minuteString = parsedValues[minuteIndex].toString().padStart(2, '0');
    const secondString = (secondIndex != null ? parsedValues[secondIndex] : 0).toString().padStart(2, '0');
    const valueString = `${yearString}-${monthString}-${dayString}T${hourString}:${minuteString}:${secondString}.${millisecondsString}`;

    return new Date(valueString);
};

/**
 * Converts a date to a iso string without timezone
 * @param date
 * @returns
 */
export const toIsoStringWithoutTimezone = (date: Date): string => {
    date.setMinutes(date.getTimezoneOffset() * -1 + date.getMinutes());
    return date.toISOString().slice(0, -1);
};

/**
 * Add seconds to a date
 * @param date
 * @param seconds
 * @returns
 */
export const addSecondsToDate = (date: Date, seconds: number) => {
    return new Date(date.getTime() + seconds * 1000);
};

/**
 * Formats milliseconds to standard time display. This cuts decimals, no rounding is done.
 * TODO: If manager needs to round the time using the rangeringsoppløsning and avrund opp/ned setting, then it should be added here
 * @param milliseconds The milliseconds we want to format
 * @param dateType A subset of the {@link DateType} that is used to determine the number of decimal places
 * @param removeEmptyParts If true, the empty parts of the time will be removed. For example, if the time is 00:01:30 and the dateType is TIMEMS1, the result will be 1:30.0 instead of 00:01:30.0
 * @returns The milliseconds formatted as a string
 */
export const millisecondsToFormattedTime = (
    milliseconds: number,
    dateType: 'TIME' | 'TIMEMS1' | 'TIMEMS2' | 'TIMEMS3',
    removeEmptyParts?: boolean,
): string => {
    const isNegative = milliseconds < 0;

    milliseconds = Math.abs(milliseconds);
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const millisecondsLeft = milliseconds % 1000;

    const formatted = `${isNegative ? '-' : ''}${hours.toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;

    if (removeEmptyParts) {
        if (hours === 0) {
            if (minutes === 0) {
                return `${isNegative ? '-' : ''}${seconds.toString()}${dateType !== 'TIME' ? '.' : ''}${millisecondsLeft
                    .toString()
                    .padStart(3, '0')
                    .substring(0, dateType === 'TIMEMS1' ? 1 : dateType === 'TIMEMS2' ? 2 : 3)}`;
            }
            return `${isNegative ? '-' : ''}${minutes.toString()}:${(seconds % 60).toString().padStart(2, '0')}${dateType !== 'TIME' ? '.' : ''}${millisecondsLeft
                .toString()
                .padStart(3, '0')
                .substring(0, dateType === 'TIMEMS1' ? 1 : dateType === 'TIMEMS2' ? 2 : 3)}`;
        }
    }

    switch (dateType) {
        case 'TIME':
            return formatted;
        case 'TIMEMS1':
            return `${formatted}.${millisecondsLeft.toString().padStart(3, '0').substring(0, 1)}`;
        case 'TIMEMS2':
            return `${formatted}.${millisecondsLeft.toString().padStart(3, '0').substring(0, 2)}`;
        case 'TIMEMS3':
            return `${formatted}.${millisecondsLeft.toString().padStart(3, '0').substring(0, 3)}`;
    }
};

/**
 * Create a date object from a UTC time. The UTC time is the number of milliseconds since 1970-01-01 00:00:00
 * @param utctime
 * @returns A Date object with the time set to the UTC time
 */
export const utcToLocalTime = (utctime: number | undefined): Date => {
    // Create a new Date object with UTC time 1970-01-01 00:00:00
    const date = new Date(0);

    if (utctime == undefined) {
        return date;
    }

    // Add the milliseconds to the date
    date.setUTCMilliseconds(utctime);

    return date;
};

/**
 * Compare two dates
 * Returns a negative number if a is before b, a positive number if a is after b and 0 if they are the same
 * @param a The first date to compare
 * @param b The second date to compare
 * @returns A negative number if a is before b, a positive number if a is after b and 0 if they are the same
 */
export const compareDateTimes = (a: Date, b: Date) => {
    return a.getTime() - b.getTime();
};

/**
 * Chcecks if two dates are the same
 * @param a
 * @param b
 * @returns
 */
export const isSame = (a: Date | null, b: Date | null) => {
    if (a == null || b == null) {
        return false;
    }
    return compareDateTimes(a, b) === 0;
};

/**
 * Compare two nullable dates
 * Returns a negative number if a is before b, a positive number if a is after b and 0 if they are the same
 * Null is considered to be the minimum value, so a NULL is always earlier than any non-null Date
 * @param a The first date to compare
 * @param b The second date to compare
 * @returns A negative number if a is before b, a positive number if a is after b and 0 if they are the same
 */
export const compareDateTimesNullable = (a: Date | null, b: Date | null) => {
    if (a == null && b == null) {
        return 0;
    }

    if (a == null) {
        return -1;
    }

    if (b == null) {
        return 1;
    }

    return a.getTime() - b.getTime();
};

/**
 * Compare timespans. Can be used for sorting
 * @param a The first timespan to compare
 * @param b The second timespan to compare
 * @returns The diff in ms
 */
export const compareTimespans = (a: ITimeSpan, b: ITimeSpan) => {
    return a.TotalMilliseconds - b.TotalMilliseconds;
};

/**
 * Compare timespans. Can be used for sorting
 * @param a The first timespan to compare
 * @param b The second timespan to compare
 * @returns The diff in ms
 */
export const compareTimespansNullable = (a: ITimeSpan | null, b: ITimeSpan | null) => {
    if (a == null && b == null) {
        return 0;
    }

    if (a == null) {
        return -1;
    }

    if (b == null) {
        return 1;
    }

    return a.TotalMilliseconds - b.TotalMilliseconds;
};

/**
 * Validate a date. Since a date object can contain a invalid date.
 * @param date
 * @returns
 */
export const isValidDate = (date: Date | null) => {
    if (date == null) {
        return false;
    }

    if (!isFinite(+date)) {
        return false;
    }

    return date.getFullYear() > 0 && date.getMonth() >= 0 && date.getDate() > 0;
};

/**
 * Will calculate the age of an athlete at a given event date based on their birthday and the sport's season start date.
 * @param birthday
 * @param eventdate
 * @param sport
 * @returns
 */
export const getEventAge = (birthday: Date | null, eventdate: Date, sport: Sport): number | null => {
    if (birthday == null) {
        return null;
    }

    const seasonStart = (sport.SeasonStart ?? '').trim() == '' ? '0101' : sport.SeasonStart;
    const hasSeasonStart = sport.SeasonStart != null && sport.SeasonStart.trim() != '';
    const month = parseInt(seasonStart!.substring(0, 2), 10) - 1; // JS months are 0-indexed
    const day = parseInt(seasonStart!.substring(2, 4), 10);

    const currentSeasonStart = new Date(eventdate.getFullYear(), month, day);

    if (eventdate > currentSeasonStart && hasSeasonStart) {
        currentSeasonStart.setFullYear(currentSeasonStart.getFullYear() + 1);
    }

    return currentSeasonStart.getFullYear() - birthday.getFullYear();
};

/**
 * Converts a local Date object to UTC time in milliseconds since 1970-01-01 00:00:00
 * @param date The local Date object we want to convert to UTC time
 */
export const getUtcTimeFromLocalDate = (date: Date): Date => {
    const utcTimestamp = date.getTime();

    // we need to adjust the timestamp to UTC time
    const adjustedTimestamp = utcTimestamp - date.getTimezoneOffset() * -60000;

    // We need to create a new Date object with the UTC time
    return new Date(adjustedTimestamp);
};
